import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'react-hot-toast';

const Unsubscribe = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('You have been successfully unsubscribed');
      setEmail('');
    } catch (error) {
      toast.error('Failed to process unsubscribe request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setEmail('');
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-lg w-full">
        {/* Paper plane illustration */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <Send className="h-16 w-16 text-green-500 transform rotate-45" />
            <div className="absolute -top-2 -right-2">
              <svg className="w-20 h-12" viewBox="0 0 80 48" fill="none">
                <path 
                  d="M10 24 Q 25 10, 40 24 T 70 24" 
                  stroke="#10b981" 
                  strokeWidth="2" 
                  strokeDasharray="4,4" 
                  fill="none"
                  opacity="0.6"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-semibold text-gray-800 text-center mb-6">
            Unsubscribe request
          </h1>
          
          <div className="space-y-4 mb-8">
            <p className="text-gray-600 leading-relaxed">
              Thank you for being connected with United eLoans. If you no longer wish to receive emails or 
              direct mail from us, please enter your email address below and click "Unsubscribe."
            </p>
            
            <p className="text-gray-600">
              <span className="font-medium">Note:</span> If you'd like to unsubscribe from our SMS/text messages instead please{' '}
              <button 
                className="text-green-600 underline hover:text-green-700 transition-colors"
                onClick={() => toast.info('Please reply STOP to any text message from us to unsubscribe from SMS')}
              >
                click here
              </button>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-700 font-medium mb-3">
                Unsubscribe from email & direct mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-gray-700 placeholder-gray-400"
                required
              />
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 px-6 py-3 text-gray-600 bg-gray-100 rounded-full font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 text-white bg-green-500 rounded-full font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Processing...' : 'Unsubscribe'}
              </button>
            </div>
          </form>
        </div>

        {/* Contact section */}
        <div className="mt-8 bg-gray-100 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-2">Contact Us</h3>
          <p className="text-gray-600 leading-relaxed">
            If you have any questions or comments regarding this policy or our practices, please reach 
            out to us at{' '}
            <a 
              href="mailto:info@unitedeloan.com" 
              className="text-blue-600 underline hover:text-blue-700 transition-colors"
            >
              info@unitedeloan.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;